"use client";

import { Suspense, useEffect, useState, type FormEvent } from "react";
import { useSearchParams } from "@/lib/compat/navigation";
import { useTranslations } from "@/lib/compat/i18n";
import { isPublic } from "@/lib/profile";

/**
 * Token entry gate. Two paths to authenticate:
 *
 *   1. URL shortcut — `?t=acme-x7k2` on mount fires GET /api/auth/token-check.
 *      Removes the token from the URL on success (history.replaceState) so
 *      it's not in browser history or copyable from the address bar.
 *
 *   2. Manual entry — token input + submit → POST /api/auth/token-check.
 *
 * On success, redirect to the `?from=` path (default `/rundemo`). On failure,
 * surface a brief error and let the visitor retry.
 *
 * Profile-aware copy (only differs in the eyebrow line + page title):
 *   - public  : "Demo access"
 *   - full    : "Private demo access"
 */
export default function EntryPage() {
  return (
    <Suspense fallback={<main className="grid min-h-screen place-items-center bg-orbitra-ink" />}>
      <EntryGate />
    </Suspense>
  );
}

function EntryGate() {
  const search = useSearchParams();
  const from   = decodeURIComponent(search.get("from") ?? "/rundemo");
  const isPub  = isPublic();
  const t      = useTranslations("authEntry");

  const [token, setToken]   = useState("");
  const [status, setStatus] = useState<"idle" | "checking" | "invalid" | "error">("idle");
  const [autoTried, setAutoTried] = useState(false);

  // After a successful token check the cookie is set in the browser.
  // Use a HARD navigation (window.location.assign) so the entry page
  // unmounts cleanly and the server re-runs middleware with the new
  // cookie on a fresh request. Soft client navigation (router.replace)
  // is unreliable here — auth-state transitions deserve a full load.
  const finishAuth = () => {
    if (typeof window !== "undefined") window.location.assign(from);
  };

  // URL-token shortcut: if `?t=...` is present, try it on mount once.
  useEffect(() => {
    const t = search.get("t");
    if (!t || autoTried) return;
    setAutoTried(true);
    setStatus("checking");
    fetch(`/api/auth/token-check?t=${encodeURIComponent(t)}`, { method: "GET", credentials: "same-origin" })
      .then(async (r) => {
        if (r.ok) {
          // Wipe the token from the URL bar before navigating away.
          window.history.replaceState({}, "", "/auth/entry");
          finishAuth();
        } else {
          setStatus("invalid");
        }
      })
      .catch(() => setStatus("error"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, autoTried]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!token) return;
    setStatus("checking");
    try {
      const r = await fetch("/api/auth/token-check", {
        method: "POST",
        credentials: "same-origin",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ token }),
      });
      if (r.ok) {
        finishAuth();
      } else {
        setStatus("invalid");
        setToken("");
      }
    } catch {
      setStatus("error");
    }
  }

  return (
    <main className="grid min-h-screen place-items-center bg-orbitra-ink p-6 text-orbitra-paper">
      <div className="w-full max-w-md">
        {/* Eyebrow + heading */}
        <p className="font-mono text-[10px] uppercase tracking-[0.32em] text-orbitra-teal-soft">
          {t("brand")}
        </p>
        <h1 className="mt-2 font-display text-3xl font-semibold leading-tight tracking-tight">
          {isPub ? t("headingPublic") : t("headingPrivate")}
        </h1>
        <p className="mt-2 text-sm text-orbitra-muted">
          {isPub ? t("subheadPublic") : t("subheadPrivate")}
        </p>

        {/* Token form */}
        <form onSubmit={onSubmit} className="mt-8 flex flex-col gap-3">
          <label className="font-mono text-[10px] uppercase tracking-[0.18em] text-orbitra-teal-soft" htmlFor="token">
            {t("tokenLabel")}
          </label>
          <input
            id="token"
            type="text"
            autoFocus
            autoComplete="off"
            spellCheck={false}
            value={token}
            onChange={(e) => { setToken(e.target.value); if (status === "invalid") setStatus("idle"); }}
            placeholder={t("tokenPlaceholder")}
            className="rounded-md border border-state-verified/40 bg-orbitra-ink/60 px-4 py-3 font-mono text-base text-orbitra-paper outline-none focus:border-state-verified/80 focus:ring-1 focus:ring-state-verified/40"
            disabled={status === "checking"}
          />

          <button
            type="submit"
            disabled={!token || status === "checking"}
            className="mt-2 cta-primary px-5 py-3 font-display text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-60"
          >
            {status === "checking" ? t("ctaVerifying") : t("ctaContinue")}
          </button>

          {status === "invalid" && (
            <p className="mt-1 text-[12px] text-policy-fail">{t("errorInvalid")}</p>
          )}
          {status === "error" && (
            <p className="mt-1 text-[12px] text-policy-fail">{t("errorServer")}</p>
          )}
        </form>

        <p className="mt-10 text-[11px] text-orbitra-muted">
          {t("footer")}
        </p>
      </div>

      {/* noindex hint so the entry page isn't search-indexed */}
      <meta name="robots" content="noindex,nofollow" />
    </main>
  );
}
